<template>
  <transition name="modal">
    <div class="modal-mask">
      <a
        class="modal-close"
        aria-label="Close"
        @click="$emit('close')"
      >
        <img
          :src="asset('/images/close.png')"
          width="35px"
        >
      </a>

      <div class="modal-wrapper">
        <div 
          class="modal-register"
        >
          <div class="modal-title">
            Register
          </div>

          <div v-show="step === 1">
            <div>
          
              <form
                id="registerForm"
                class="form-horizontal"
                role="form"
                method="POST"
                @submit.prevent="postLogin"
              >
                <span v-text="response" />

                <div class="row">
                  <div class="col-sm-6">
                    <div
                      class="form-group"
                      :class="errors.first_name ? ' has-error' : ''"
                    >
                      <label class="register-label">First name<sup>*</sup></label>
                      <input
                        id="register-first-name"
                        class="form-control"
                        name="first_name"
                        v-model="first_name"
                        autofocus
                      >
                      <span
                        class="help-block"
                        v-if="errors.first_name"
                        :errors="errors.first_name"
                      >
                        <span
                          v-for="error in errors.first_name"
                          :key="error"
                        >{{ error }}</span>
                      </span>
                    </div>
                  </div>

                  <div class="col-sm-6">
                    <div
                      class="form-group"
                      :class="errors.last_name ? ' has-error' : ''"
                    >
                      <label class="register-label">Last name<sup>*</sup></label>
                      <input
                        id="register-last-name"
                        class="form-control"
                        name="last_name"
                        v-model="last_name"
                        autofocus
                      >

                      <span
                        class="help-block"
                        v-if="errors.last_name"
                        :errors="errors.last_name"
                      >
                        <span
                          v-for="error in errors.last_name"
                          :key="error"
                        >{{ error }}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-sm-12">
                    <div
                      class="form-group"
                      :class="errors.email ? ' has-error' : ''"
                    >
                      <label class="register-label">Email address<sup>*</sup></label>
                      <input
                        id="register-email"
                        type="email"
                        class="form-control"
                        name="email"
                        v-model="email"
                        autofocus
                      >
                      <span
                        class="help-block"
                        v-if="errors.email"
                        :errors="errors.email"
                      >
                        <span
                          v-for="error in errors.email"
                          :key="error"
                        >{{ error }}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="row d-sm-flex align-items-end">
                  <div class="col-sm-6">
                    <div
                      class="form-group"
                      :class="errors.password ? ' has-error' : ''"
                    >
                      <label class="register-label">
                        Password<sup>*</sup>
                        <span class="hint">Must contain at least 8 characters and include a number.</span>
                      </label>
                      <div class="relative">
                        <input
                          id="register-password"
                          class="form-control"
                          :type="showPassword ? 'input' : 'password'"
                          v-model="password"
                          name="password"
                          required
                        >
                        <img
                          class="eye-icon" 
                          :src="showPassword ? '/images/icons/eye.svg' : '/images/icons/eye-crossed.svg'" 
                          alt="eye"
                          @click="showPassword = !showPassword"
                        >
                      </div>
                      <span
                        class="help-block"
                        v-if="errors.password"
                        :errors="errors.password"
                      >
                        <span
                          v-for="error in errors.password"
                          :key="error"
                        >
                          {{ error }}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div class="col-sm-6 align-self-start">
                    <div class="form-group">
                      <label class="register-label">Reenter Password<sup>*</sup></label>
                      <div class="relative">
                        <input
                          id="register-password-confirm"
                          class="form-control"
                          :type="showRepeatPassword ? 'input' : 'password'"
                          name="password_confirmation"
                          v-model="password_confirmation"
                          required
                        >
                        <img
                          class="eye-icon" 
                          :src="showRepeatPassword ? '/images/icons/eye.svg' : '/images/icons/eye-crossed.svg'" 
                          alt="eye"
                          @click="showRepeatPassword = !showRepeatPassword"
                        >
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-sm-6">
                    <div
                      class="form-group"
                      :class="errors.organisation ? ' has-error' : ''"
                    >
                      <label class="register-label">Organisation/School<sup>*</sup></label>
                      <input
                        id="register-organisation"
                        class="form-control"
                        name="organisation"
                        v-model="organisation"
                        required
                      >
                      <span
                        class="help-block"
                        v-if="errors.organisation"
                        :errors="errors.organisation"
                      >
                        <span
                          v-for="error in errors.organisation"
                          :key="error"
                        >{{ error }}</span>
                      </span>
                    </div>
                  </div>

                  <div class="col-sm-6">
                    <Dropdown 
                      name="role"
                      placeholder="Select occupation..."
                      label="Occupation"
                      class="registration"
                      :options="role_options"
                      :errors="errors.role"
                      :openDirection="'top'"
                      @select="(option) => role = option"
                    />
                  </div>
                </div>

                <div class="row">
                  <div class="col-sm-6">
                    <Dropdown 
                      name="state"
                      placeholder="Select state..."
                      label="State"
                      class="mb-0 registration"
                      :options="states_options"
                      :errors="errors.state"
                      :openDirection="'top'"
                      @select="(option) => state = option"
                    />
                  </div>

                  <div class="col-sm-6">
                    <div
                      class="form-group mb-0"
                      :class="errors.country ? ' has-error' : ''"
                    >
                      <label class="register-label">Country<sup>*</sup></label>
                      <input
                        id="register-country"
                        type="text"
                        class="form-control"
                        name="country"
                        v-model="country"
                        autofocus
                        readonly
                      >
                    </div>
                    <span
                      class="help-block"
                      v-if="errors.country"
                      :errors="errors.country"
                    >
                      <span
                        v-for="error in errors.country"
                        :key="error"
                      >{{ error }}</span>
                    </span>
                  </div>
                </div>

                <slot name="form" />
              </form>
              <div class="mt-2 mt-xxl-4 d-flex">
                <div class="next-wrapper w-50">
                  <button
                    type="submit"
                    @click.prevent="validate"
                    class="btn btn-success btn-lg ok-btn"
                  >
                    Next
                  </button>
                </div>

                <div class="cancel-wrapper w-50">
                  <button
                    type="button"
                    @click.prevent="$emit('close')"
                    class="btn btn-danger btn-lg ok-btn"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
            
          </div>
          <div v-show="step === 2">
            <div class="modal-body">
              <div class="row">
                  <div class="col-md-12">
                    <div class="form-group">
                      <div class="terms-and-conditions">
                        <b>Terms and Conditions</b><br>
                        <p class="terms-body" v-html="terms" />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group">
                      <div class="checkbox-wrapper">
                        <div class="squaredCheckbox">
                          <input
                            v-model="accept"
                            type="checkbox"
                            id="accept"
                            name="accept"
                            aria-label="Accept terms and conditions"
                            value="1"
                          >
                          <label for="accept" />
                        </div>
                        <label
                          for="accept"
                          class="agree-label"
                        >
                          I have read and accept the terms and conditions.
                        </label>
                      </div>

                      <span
                        class="mt-1 help-block"
                        v-if="errors.accept"
                        :errors="errors.accept"
                      >
                        <span
                          v-for="error in errors.accept"
                          :key="error"
                        >{{ error }}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group checkbox-wrapper">
                      <div class="squaredCheckbox">
                        <input
                          v-model="newsletter"
                          type="checkbox"
                          id="newsletter"
                          aria-label="I would like to receive updates about the app."
                          value="1"
                        >
                        <label for="newsletter" />
                      </div>
                      <label
                        for="newsletter"
                        class="agree-label"
                      >
                        I would like to receive updates about the app.
                      </label>
                    </div>
                  </div>
                </div>
            </div>
            
            <div class="mt-2 mt-xxl-4 d-flex">
              <div class="next-wrapper w-50">
                <button
                  type="submit"
                  @click.prevent="register"
                  class="btn btn-success btn-lg ok-btn"
                >
                Register
                </button>
              </div>

              <div class="cancel-wrapper w-50">
                <button
                  type="button"
                  @click.prevent="step = 1"
                  class="btn btn-danger btn-lg ok-btn"
                >
                  Cancel
                </button>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Dropdown from '../app/forms/Dropdown.vue'

export default {
  name: 'Register',

  components: {
    Dropdown
  },

  data: function () {
    const statesOptions = window.states.reduce((acc, item) => {
      acc.push({
        label: item,
        value: item
      });

      return acc;
    }, []);

    const rolesOptions = window.roles.reduce((acc, item) => {
      acc.push({
        label: item,
        value: item
      });

      return acc;
    }, []);
    
    return {
      showPassword: false,
      showRepeatPassword: false,
      step: 1,
      data: {},
      response: '',
      errors: [],
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password_confirmation: '',
      organisation: '',
      role: '',
      region: '',
      state: '',
      country: 'Australia',
      accept: false,
      newsletter: true,
      states_options: statesOptions,
      role_options: rolesOptions,
    }
  },
  computed: {
    terms () {
      if (this.$store.state.terms !== null) {
        return this.$store.state.terms
      }
      return 'T & C'
    }
  },
  methods: {
    validate: function () {
      let self = this;
      this.response = ''
      this.errors = []
      this.data = {
        first_name: this.first_name,
        last_name: this.last_name,
        email: this.email,
        password: this.password,
        password_confirmation: this.password_confirmation,
        organisation: this.organisation,
        role: this.role,
        region: this.region,
        state: this.state,
        country: this.country,
        my_name: document.getElementById('registerForm').elements['my_name'].value,
        my_time: document.getElementById('registerForm').elements['my_time'].value
      }
      this.$parent.callAPI('POST', '/validate', this.data)
        .then(function (response) {
          self.step = 2;
        }, error => {
          if (error.response.status === 422) {
            self.errors = error.response.data.errors
          } else {
            self.response = 'Did not receive a response. Please try again in a few minutes'
          }
        })
    },

    register() {
      let self = this;
      self.data.accept = this.accept;
      self.data.newsletter = this.newsletter;
      this.$parent.callAPI('POST', '/register', self.data)
        .then(function (response) {
          self.$emit('close');
          window.location = `${window.rootPath}/login?showConfirmModal=1&type=postRegister&user_id=${response.data.user.id}`;
        }, error => {
          if (error.response.status === 422) {
            self.errors = error.response.data.errors
          } else {
            self.response = 'Did not receive a response. Please try again in a few minutes'
          }
        })
    }

  }
}
</script>

<style scoped lang="scss">
@import '../../sass/_variables.scss';

.help-block {
  padding-left: 0px;

  @media (min-width: $breakpoint-xxl) {
    font-size: 30px;
  }
}

.hint {
  font-size: 12px;
  @media (min-width: $breakpoint-xxl) {
    font-size: 24px;
  }
}

.form-control {
  height: 45px;
  color: #2E3C54;
  padding-left: 20px;

  @media (min-width: $breakpoint-xxl) {
    border-radius: 20px;
    height: 112px;
    font-size: 40px;
    padding-left: 40px;
    padding-right: 40px;
  }
}

.form-group {
  margin-top: 5px;
  @media (min-width: $breakpoint-xxl) {
    margin-top: 10px;
  }
}

.col-md-6, .col-md-12 {
  @media (min-width: $breakpoint-xxl) {
    padding-left: 43px;
    padding-right: 43px
  }
}

.modal-title {
  font-size: 20pt;
  font-weight: 700; 
  @media (min-width: $breakpoint-xxl) {
    font-size: 80px;
  }
}

:-webkit-autofill,
:-webkit-autofill:hover, 
:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px #FFF inset;
}

input:-webkit-autofill::first-line {
  font-size: 20px;
  @media (min-width: $breakpoint-xxl) {
    font-size: 50px !important;
  }
}

.next-wrapper {
  display: flex;
  justify-content: flex-end;
  padding-right: 15px;

  @media (min-width: $breakpoint-xxl) {
    padding-right: 45px;
  }
}

.cancel-wrapper {
  padding-left: 15px;

  @media (min-width: $breakpoint-xxl) {
    padding-left: 45px;
  }
}

.ok-btn {
  width: 50%;
  @media (min-width: $breakpoint-xxl) {
    width: 63%;
  }
}

.form-group {
  margin-left: 0;
  margin-right: 0;
}

.agree-label {
  font-weight: 400;
  font-size: 20px;
  margin-left: 10px;
  margin-bottom: 0;
  @media (min-width: $breakpoint-xxl) {
    margin-top: 20px;
    font-size: 40px;
    margin-left: 40px;
  }
}

.squaredCheckbox label {
  width: 42px;

  @media (min-width: $breakpoint-xxl) {
    min-width: 48px;
    height: 48px;
  }
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
}

.eye-icon {
  top: 25%;

  @media (min-width: $breakpoint-xxl) {
    width: 65px;
    height: 46px;
    top: 30%;
  }
}

.col-md-6 {
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 5px;
}

.modal-register {
  max-height: 95vh;
  overflow-y: auto;
  width: 95%;

  @media (min-width: $breakpoint-md) {
    width: 900px;
  }

  @media (min-width: $breakpoint-lg) {
    width: 75%;
  }
}

.terms-and-conditions {
  padding: 15px 20px;
  color: $text-color;
  background-color: #fff;
  border-radius: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  height: 330px;

  @media (min-width: $breakpoint-xxl) {
    height: 38vh;
    padding: 52px 70px 80px 70px;
    border-radius: 20px;
  }
}


</style>
